import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import { Bold, Italic, Heading2, Heading3, List, ListOrdered, Link2, ImageIcon, Quote, Undo2, Redo2 } from "lucide-react";

/**
 * Toolbar button — defined outside the editor component so React doesn't
 * remount it on every editor update.
 */
function TbBtn({ active, onClick, label, children, testId }) {
  return (
    <button type="button"
            onClick={onClick}
            data-testid={testId}
            title={label}
            aria-label={label}
            className={`p-2 rounded-sm text-sm transition-colors ${
              active
                ? "bg-[#b8963e]/15 text-[#b8963e]"
                : "text-[#c9bca4] hover:text-[#f4efe6] hover:bg-[#2a2620]"
            }`}>
      {children}
    </button>
  );
}

/**
 * Lightweight TipTap WYSIWYG bound to the publication body_html field.
 *
 * Usage:
 *   <RichTextEditor value={form.body_html} onChange={(html) => setForm({...form, body_html: html})} />
 */
export default function RichTextEditor({ value, onChange, placeholder = "Write the publication body…" }) {
  const editor = useEditor({
    extensions: [
      // StarterKit bundles a link mark — disable it and use the dedicated
      // Link extension below so we can configure openOnClick + HTMLAttributes.
      StarterKit.configure({ link: false }),
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: "noopener noreferrer" } }),
      Image,
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class: "research-body min-h-[300px] focus:outline-none text-[#c9bca4] p-4",
        "data-testid": "rich-text-editor-content",
      },
    },
    onUpdate({ editor }) {
      onChange?.(editor.getHTML());
    },
  });

  // External value changes (e.g. when loading an existing publication into the edit form)
  // should sync into the editor without breaking caret position during typing.
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value !== undefined && value !== current) {
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  if (!editor) return null;

  return (
    <div className="border border-[#2a2620] rounded-sm bg-[#0c0a08]" data-testid="rich-text-editor">
      <div className="flex flex-wrap items-center gap-1 px-2 py-2 border-b border-[#2a2620]">
        <TbBtn label="Bold"    active={editor.isActive("bold")}    onClick={() => editor.chain().focus().toggleBold().run()} testId="rte-bold"><Bold className="w-4 h-4" /></TbBtn>
        <TbBtn label="Italic"  active={editor.isActive("italic")}  onClick={() => editor.chain().focus().toggleItalic().run()} testId="rte-italic"><Italic className="w-4 h-4" /></TbBtn>
        <span className="w-px h-5 bg-[#2a2620] mx-1" />
        <TbBtn label="Heading 2" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} testId="rte-h2"><Heading2 className="w-4 h-4" /></TbBtn>
        <TbBtn label="Heading 3" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} testId="rte-h3"><Heading3 className="w-4 h-4" /></TbBtn>
        <TbBtn label="Bullet list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} testId="rte-bullet"><List className="w-4 h-4" /></TbBtn>
        <TbBtn label="Ordered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} testId="rte-ordered"><ListOrdered className="w-4 h-4" /></TbBtn>
        <TbBtn label="Block quote" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()} testId="rte-quote"><Quote className="w-4 h-4" /></TbBtn>
        <span className="w-px h-5 bg-[#2a2620] mx-1" />
        <TbBtn label="Link" active={editor.isActive("link")} onClick={() => {
          const prev = editor.getAttributes("link").href || "";
          const url = window.prompt("Link URL", prev);
          if (url === null) return;
          if (url === "") return editor.chain().focus().unsetLink().run();
          editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
        }} testId="rte-link"><Link2 className="w-4 h-4" /></TbBtn>
        <TbBtn label="Image" onClick={() => {
          const url = window.prompt("Image URL (full https:// link, or a data:image/... URL)");
          if (url) editor.chain().focus().setImage({ src: url }).run();
        }} testId="rte-image"><ImageIcon className="w-4 h-4" /></TbBtn>
        <span className="w-px h-5 bg-[#2a2620] mx-1" />
        <TbBtn label="Undo" onClick={() => editor.chain().focus().undo().run()} testId="rte-undo"><Undo2 className="w-4 h-4" /></TbBtn>
        <TbBtn label="Redo" onClick={() => editor.chain().focus().redo().run()} testId="rte-redo"><Redo2 className="w-4 h-4" /></TbBtn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
